import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@vvoyages/shared-ui/ui';
import { z } from 'zod';

import Button from '@/components/Button';
import { BackButton } from '@/components/PredefinedButtons';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import useAppUiData from '@/hooks/useAppUiData';
import { selectResources } from '@/reducers/common/selectors';

import { CLAIM_BOOKING_OTP_RESOURCES } from './ClaimBookingOtp.resources';
import styles from './ClaimBookingStep.module.scss';

import { StepData } from '../types/ClaimBookingTypes';
import { ClaimBookingOTPStatus, OtpVerificationResult } from '../types/LookupBookingForClaimTypes';

const otpSent = {
  subtitle: 'We sent your one-time passcode to ',
  resendSubtitle: "Didn't receive the code?",
  resend: 'Resend',
};

const otpSchema = z.object({
  otpCode: z
    .string()
    .min(1, 'Required')
    .regex(/^[0-9]+$/, 'Must be only digits')
    .min(6, 'Must be minimum 6 digits')
    .max(6, 'Must be maximum 6 digits'),
});

type OtpFormValues = z.infer<typeof otpSchema>;

type ClaimBookingOtpSentProps = {
  tabId: string;
  data: StepData;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (value: StepData & { otpCode: string }) => Promise<OtpVerificationResult>;
  onBack: () => void;
  onCancel: () => void;
  onResendOtp: () => void;
};

const ClaimBookingOtpSent: React.FC<ClaimBookingOtpSentProps> = ({
  tabId,
  data,
  onSubmit,
  onBack,
  onCancel,
  onResendOtp,
}) => {
  const appUi = useAppUiData();
  const resources = useSelector(selectResources) as Record<string, string>;
  const invalidOtpMessage =
    resources[CLAIM_BOOKING_OTP_RESOURCES.invalidOtp.id] || CLAIM_BOOKING_OTP_RESOURCES.invalidOtp.fallbackString;
  const { selectedMethod } = data;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { isSubmitting },
  } = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    mode: 'onSubmit',
    defaultValues: { otpCode: '' },
  });

  useEffect(() => {
    if (data.otpError) {
      setError('otpCode', { type: 'server', message: invalidOtpMessage });
    } else {
      clearErrors('otpCode');
    }
  }, [data.otpError, invalidOtpMessage, setError, clearErrors]);

  const values = watch();
  const isFormFilled = Object.values(values).every(Boolean);
  const isSubmitDisabled = !isFormFilled || isSubmitting;

  const handleResend = () => {
    setValue('otpCode', '');
    onResendOtp();
  };

  const onFormSubmit = handleSubmit(async ({ otpCode }) => {
    const result = await onSubmit({
      ...data,
      otpCode,
      otpAttempts: (data.otpAttempts || 0) + 1,
    });

    // react-hook-form wipes manually set errors on every submit, so the server error has to be
    // re-applied from the response - the step data alone does not change when the message repeats.
    if (result?.status === ClaimBookingOTPStatus.INVALID_OTP || result?.status === ClaimBookingOTPStatus.ERROR) {
      setError('otpCode', { type: 'server', message: invalidOtpMessage });
    }
  });

  return (
    <WizardStep tabId={tabId}>
      <BackButton onClick={onBack} className={styles.backButton} />
      <h2 className={styles.title}>{appUi[appUiIds.claimBooking.enterPasscodeHeading]}</h2>
      <p className={styles.subtitle}>
        {otpSent.subtitle} <strong>{selectedMethod?.displayValue}</strong>
      </p>
      <form onSubmit={onFormSubmit} className={styles.otpSentForm} noValidate>
        <div className={styles.claimForm}>
          <div className={styles.resendContainer}>
            <p className={styles.resendSubtitle}>{otpSent.resendSubtitle}</p>
            <Button variant="link" onClick={handleResend} className={styles.resendButton}>
              {otpSent.resend}
            </Button>
          </div>
          <div className={styles.inputWithToolTip}>
            <TextField
              name="otpCode"
              control={control}
              label="Enter your 6-digit code"
              required
              className={styles.claimBookingInput}
            />
          </div>
          <Button type="submit" variant="primary" disabled={isSubmitDisabled}>
            {appUi[appUiIds.commonLabels.next]}
          </Button>
          <Button onClick={onCancel} className={styles.cancelLink} disabled={isSubmitting}>
            {appUi[appUiIds.commonLabels.cancel]}
          </Button>
        </div>
      </form>
    </WizardStep>
  );
};

export default ClaimBookingOtpSent;
